import { assertDocumentTemplateFamilyAdmission } from "@cogo/schema/document-template-family-admission";
import type {
  CogoDocumentTemplateFamily,
  CogoDocumentTemplateFamilySlot,
  CogoDocumentTemplateFamilyVariant,
} from "@cogo/schema/document-template-family-runtime";
import type {
  CogoDocumentModelAdapter,
  CogoDocumentModelAdapterResult,
  CogoDocumentModelTelemetry,
} from "@cogo/schema/document-template-model-runner";
import {
  sha256CanonicalJson,
  sha256DocumentTemplateText,
} from "@cogo/schema/document-template-runtime";

export const COGO_DOCUMENT_TEMPLATE_FAMILY_MODEL_PATCH_POLICY = {
  schemaVersion: "cogo.document-template-family-model-patch-policy.v1",
  allowedFields: ["semanticRole", "bindingRef"],
  exactlyOneSelectedSlot: true,
  typedPatchOnly: true,
  canonicalAuthority: false,
  finalWriterAllowed: false,
  requiresHumanReview: true,
  automatedPromotionAllowed: false,
  rawModelOutputStored: false,
  reasoningTraceStored: false,
} as const;

export type CogoDocumentTemplateFamilyPatchField =
  | "semanticRole"
  | "bindingRef";

export interface CogoDocumentTemplateFamilySlotPatch {
  slotId: string;
  field: CogoDocumentTemplateFamilyPatchField;
  value: string;
  reason: string;
}

export interface CogoDocumentTemplateFamilyModelProposal {
  schemaVersion: "cogo.document-template-family-model-proposal.v1";
  proposalId: string;
  status: "proposed";
  createdAt: string;
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  patch: CogoDocumentTemplateFamilySlotPatch;
  patchHash: string;
  modelInvocationReceiptId: string;
  canonicalAuthority: false;
  finalWriterAllowed: false;
  requiresHumanReview: true;
  automatedPromotionAllowed: false;
}

type UnavailableTelemetry =
  | "served-model-version"
  | "provider-request-id"
  | "token-usage"
  | "cost"
  | "latency";

export interface CogoDocumentTemplateFamilyModelInvocationReceipt {
  schemaVersion: "cogo.document-template-family-model-invocation-receipt.v1";
  receiptId: string;
  generatedAt: string;
  invocationMode: "live-api";
  status: "completed";
  provider: string;
  model: string;
  servedModelVersion: string | null;
  providerRequestId: string | null;
  tokenUsage: CogoDocumentModelTelemetry["tokenUsage"];
  costUsd: number | null;
  latencyMs: number | null;
  telemetryCompleteness: "complete" | "partial";
  unavailableTelemetry: UnavailableTelemetry[];
  promptRef: string;
  promptHash: string;
  instructionHash: string;
  inputContextHash: string;
  policyRef: string;
  policyHash: string;
  outputRef: string;
  outputArtifactHash: string;
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  slotId: string;
  field: CogoDocumentTemplateFamilyPatchField;
  allowedEdit: {
    scope: "single-slot-field";
    editKind: "upsert-semantic-slot" | "set-slot-binding";
    variantId: string;
    slotId: string;
    field: CogoDocumentTemplateFamilyPatchField;
    immutableContextHash: string;
    outputArtifactHash: string;
  };
  canonicalAuthority: false;
  finalWriterAllowed: false;
  requiresHumanReview: true;
  automatedPromotionAllowed: false;
  rawModelOutputStored: false;
  reasoningTraceStored: false;
  claimBoundary: string;
  receiptHash: string;
}

export async function hashCogoDocumentTemplateFamilyModelInvocationReceipt(
  receipt: Omit<
    CogoDocumentTemplateFamilyModelInvocationReceipt,
    "receiptHash"
  >,
): Promise<string> {
  return sha256CanonicalJson(receipt);
}

export async function hashCogoDocumentTemplateFamilyPatchImmutableContext(input: {
  field: CogoDocumentTemplateFamilyPatchField;
  slot: CogoDocumentTemplateFamilySlot;
  binding?: CogoDocumentTemplateFamilyVariant["slotBindings"][number];
}): Promise<string> {
  if (input.field === "semanticRole") {
    const { semanticRole: _semanticRole, ...immutableSlot } = input.slot;
    return sha256CanonicalJson(immutableSlot);
  }
  if (!input.binding) {
    throw new Error("bindingRef context requires the selected slot binding.");
  }
  const { bindingRef: _bindingRef, ...immutableBinding } = input.binding;
  return sha256CanonicalJson({
    slot: input.slot,
    binding: immutableBinding,
  });
}

export interface RunCogoDocumentTemplateFamilySlotProposalResult {
  proposal: CogoDocumentTemplateFamilyModelProposal;
  receipt: CogoDocumentTemplateFamilyModelInvocationReceipt;
  prompt: string;
}

export interface RunCogoDocumentTemplateFamilySlotProposalInput {
  adapter: CogoDocumentModelAdapter;
  proposalId: string;
  createdAt: string;
  family: CogoDocumentTemplateFamily;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  slotId: string;
  field: CogoDocumentTemplateFamilyPatchField;
  instruction: string;
  promptRef?: string;
  policyRef?: string;
  outputRef?: string;
}

interface SelectedFamilySlotContext {
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  layoutProfile: string;
  slotId: string;
  slotKind: string;
  field: CogoDocumentTemplateFamilyPatchField;
  currentValue: string;
}

const PATCH_KEYS = ["field", "reason", "slotId", "value"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function requiredRef(value: string | undefined, fallback: string): string {
  const resolved = value?.trim() || fallback;
  if (!resolved)
    throw new Error("Model proposal receipt refs must be non-empty.");
  return resolved;
}

function normalizeTelemetry(result: CogoDocumentModelAdapterResult): {
  provider: string;
  model: string;
  servedModelVersion: string | null;
  providerRequestId: string | null;
  tokenUsage: CogoDocumentModelTelemetry["tokenUsage"];
  costUsd: number | null;
  latencyMs: number | null;
} {
  if (!result.provider.trim() || !result.model.trim()) {
    throw new Error("Model adapter must identify its provider and model.");
  }
  return {
    provider: result.provider,
    model: result.model,
    servedModelVersion: result.servedModelVersion ?? null,
    providerRequestId: result.providerRequestId ?? null,
    tokenUsage: result.tokenUsage ?? null,
    costUsd: result.costUsd ?? null,
    latencyMs: result.latencyMs ?? null,
  };
}

function unavailableTelemetry(
  telemetry: ReturnType<typeof normalizeTelemetry>,
): UnavailableTelemetry[] {
  return [
    ...(telemetry.servedModelVersion === null
      ? (["served-model-version"] as const)
      : []),
    ...(telemetry.providerRequestId === null
      ? (["provider-request-id"] as const)
      : []),
    ...(telemetry.tokenUsage === null ? (["token-usage"] as const) : []),
    ...(telemetry.costUsd === null ? (["cost"] as const) : []),
    ...(telemetry.latencyMs === null ? (["latency"] as const) : []),
  ];
}

export function createCogoDocumentTemplateFamilyPatchResponseSchema(input: {
  slotId: string;
  field: CogoDocumentTemplateFamilyPatchField;
}): Record<string, unknown> {
  return {
    $schema: "https://json-schema.org/draft/2020-12/schema",
    type: "object",
    additionalProperties: false,
    required: ["slotId", "field", "value", "reason"],
    properties: {
      slotId: { const: input.slotId },
      field: { const: input.field },
      value: { type: "string", minLength: 1 },
      reason: { type: "string", minLength: 1 },
    },
  };
}

export function parseCogoDocumentTemplateFamilySlotPatch(
  text: string,
  selection: {
    slotId: string;
    field: CogoDocumentTemplateFamilyPatchField;
  },
): CogoDocumentTemplateFamilySlotPatch {
  const payload = text.trim();
  if (!payload.startsWith("{") || !payload.endsWith("}")) {
    throw new Error(
      "Model family patch must be exactly one JSON object with no prose or Markdown.",
    );
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload) as unknown;
  } catch (error) {
    throw new Error(
      `Model family patch is not valid JSON: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );
  }
  if (!isRecord(parsed)) {
    throw new Error("Model family patch must be a JSON object.");
  }
  const keys = Object.keys(parsed).sort();
  if (JSON.stringify(keys) !== JSON.stringify(PATCH_KEYS)) {
    throw new Error(
      "Model family patch must contain exactly slotId, field, value, and reason.",
    );
  }
  if (parsed.slotId !== selection.slotId) {
    throw new Error(
      "Model family patch slotId does not match the selected slot.",
    );
  }
  if (parsed.field !== selection.field) {
    throw new Error(
      "Model family patch field does not match the selected field.",
    );
  }
  if (typeof parsed.value !== "string" || !parsed.value.trim()) {
    throw new Error("Model family patch value must be a non-empty string.");
  }
  if (typeof parsed.reason !== "string" || !parsed.reason.trim()) {
    throw new Error("Model family patch reason must be a non-empty string.");
  }
  return {
    slotId: selection.slotId,
    field: selection.field,
    value: parsed.value,
    reason: parsed.reason,
  };
}

export function buildCogoDocumentTemplateFamilySlotProposalPrompt(input: {
  context: SelectedFamilySlotContext;
  instruction: string;
}): string {
  return [
    "You are a bounded Coogo Template Family slot-patch proposer.",
    "Return exactly one JSON object with only slotId, field, value, and reason. Do not return Markdown, prose outside the object, or tool calls.",
    `The slotId must be ${JSON.stringify(input.context.slotId)} and field must be ${JSON.stringify(input.context.field)}.`,
    "You may propose a value only for that selected field. You may not edit another slot or variant, write a final artifact, apply the patch, claim canonical authority, or promote the family.",
    `FAMILY_ID: ${input.context.familyId}`,
    `FAMILY_VERSION: ${input.context.familyVersion}`,
    `FAMILY_REVISION: ${input.context.familyRevision}`,
    `FAMILY_HASH: ${input.context.familyHash}`,
    `VARIANT_ID: ${input.context.variantId}`,
    `LAYOUT_PROFILE: ${input.context.layoutProfile}`,
    `SLOT_ID: ${input.context.slotId}`,
    `SLOT_KIND: ${input.context.slotKind}`,
    `FIELD: ${input.context.field}`,
    `CURRENT_VALUE: ${JSON.stringify(input.context.currentValue)}`,
    "USER_INSTRUCTION:",
    input.instruction,
  ].join("\n");
}

export async function runCogoDocumentTemplateFamilySlotProposal(
  input: RunCogoDocumentTemplateFamilySlotProposalInput,
): Promise<RunCogoDocumentTemplateFamilySlotProposalResult> {
  if (!input.proposalId.trim()) throw new Error("proposalId is required.");
  if (Number.isNaN(Date.parse(input.createdAt))) {
    throw new Error("createdAt must be an ISO date-time.");
  }
  if (!Number.isInteger(input.familyRevision) || input.familyRevision < 1) {
    throw new Error("familyRevision must be a positive integer.");
  }
  if (!isSha256(input.familyHash)) {
    throw new Error("familyHash must be SHA-256.");
  }
  if (input.field !== "semanticRole" && input.field !== "bindingRef") {
    throw new Error("field must be semanticRole or bindingRef.");
  }
  if (!input.instruction.trim()) throw new Error("instruction is required.");

  assertDocumentTemplateFamilyAdmission(input.family);
  const observedFamilyHash = await sha256CanonicalJson(input.family);
  if (observedFamilyHash !== input.familyHash) {
    throw new Error(
      "Stale familyHash input: the supplied hash does not match the admitted family.",
    );
  }

  const variant = input.family.variants.find(
    (candidate) => candidate.variantId === input.variantId,
  );
  if (!variant) {
    throw new Error(
      `Unknown document template family variant: ${input.variantId}`,
    );
  }
  const slot = input.family.semanticCore.slots.find(
    (candidate) => candidate.id === input.slotId,
  );
  if (!slot) {
    throw new Error(`Unknown document template family slot: ${input.slotId}`);
  }
  const binding = variant.slotBindings.find(
    (candidate) => candidate.slotId === input.slotId,
  );
  if (input.field === "bindingRef" && !binding) {
    throw new Error(
      `Variant ${input.variantId} does not bind slot ${input.slotId}; bindingRef cannot be proposed.`,
    );
  }

  const context: SelectedFamilySlotContext = {
    familyId: input.family.familyId,
    familyVersion: input.family.familyVersion,
    familyRevision: input.familyRevision,
    familyHash: input.familyHash,
    variantId: variant.variantId,
    layoutProfile: variant.layoutProfile,
    slotId: slot.id,
    slotKind: slot.kind,
    field: input.field,
    currentValue:
      input.field === "semanticRole" ? slot.semanticRole : binding!.bindingRef,
  };
  const prompt = buildCogoDocumentTemplateFamilySlotProposalPrompt({
    context,
    instruction: input.instruction,
  });
  const responseJsonSchema =
    createCogoDocumentTemplateFamilyPatchResponseSchema({
      slotId: input.slotId,
      field: input.field,
    });
  const invoked = await input.adapter.invoke({ prompt, responseJsonSchema });
  const patch = parseCogoDocumentTemplateFamilySlotPatch(invoked.text, {
    slotId: input.slotId,
    field: input.field,
  });
  const patchHash = await sha256CanonicalJson(patch);
  const immutableContextHash =
    await hashCogoDocumentTemplateFamilyPatchImmutableContext({
      field: input.field,
      slot,
      ...(binding ? { binding } : {}),
    });
  const promptHash = await sha256DocumentTemplateText(prompt);
  const instructionHash = await sha256DocumentTemplateText(input.instruction);
  const inputContextHash = await sha256CanonicalJson({
    ...context,
    instructionHash,
  });
  const receiptId = `${input.proposalId}.invocation`;
  const telemetry = normalizeTelemetry(invoked);
  const unavailable = unavailableTelemetry(telemetry);
  const promptRef = requiredRef(
    input.promptRef,
    `workspace://document-template-family/${input.family.familyId}/slot-patch.prompt`,
  );
  const policyRef = requiredRef(
    input.policyRef,
    "policy://cogo/document-template-family/model-slot-patch/v1",
  );
  const outputRef = requiredRef(
    input.outputRef,
    `workspace://document-template-family/${input.family.familyId}/proposals/${input.proposalId}`,
  );

  const proposal: CogoDocumentTemplateFamilyModelProposal = {
    schemaVersion: "cogo.document-template-family-model-proposal.v1",
    proposalId: input.proposalId,
    status: "proposed",
    createdAt: input.createdAt,
    familyId: input.family.familyId,
    familyVersion: input.family.familyVersion,
    familyRevision: input.familyRevision,
    familyHash: input.familyHash,
    variantId: variant.variantId,
    patch,
    patchHash,
    modelInvocationReceiptId: receiptId,
    canonicalAuthority: false,
    finalWriterAllowed: false,
    requiresHumanReview: true,
    automatedPromotionAllowed: false,
  };
  const receiptBasis: Omit<
    CogoDocumentTemplateFamilyModelInvocationReceipt,
    "receiptHash"
  > = {
    schemaVersion: "cogo.document-template-family-model-invocation-receipt.v1",
    receiptId,
    generatedAt: input.createdAt,
    invocationMode: "live-api",
    status: "completed",
    provider: telemetry.provider,
    model: telemetry.model,
    servedModelVersion: telemetry.servedModelVersion,
    providerRequestId: telemetry.providerRequestId,
    tokenUsage: telemetry.tokenUsage,
    costUsd: telemetry.costUsd,
    latencyMs: telemetry.latencyMs,
    telemetryCompleteness: unavailable.length === 0 ? "complete" : "partial",
    unavailableTelemetry: unavailable,
    promptRef,
    promptHash,
    instructionHash,
    inputContextHash,
    policyRef,
    policyHash: await sha256CanonicalJson(
      COGO_DOCUMENT_TEMPLATE_FAMILY_MODEL_PATCH_POLICY,
    ),
    outputRef,
    outputArtifactHash: patchHash,
    familyId: input.family.familyId,
    familyVersion: input.family.familyVersion,
    familyRevision: input.familyRevision,
    familyHash: input.familyHash,
    variantId: variant.variantId,
    slotId: slot.id,
    field: input.field,
    allowedEdit: {
      scope: "single-slot-field",
      editKind:
        input.field === "semanticRole"
          ? "upsert-semantic-slot"
          : "set-slot-binding",
      variantId: variant.variantId,
      slotId: slot.id,
      field: input.field,
      immutableContextHash,
      outputArtifactHash: patchHash,
    },
    canonicalAuthority: false,
    finalWriterAllowed: false,
    requiresHumanReview: true,
    automatedPromotionAllowed: false,
    rawModelOutputStored: false,
    reasoningTraceStored: false,
    claimBoundary:
      "This receipt proves one typed slot-field proposal bound to an admitted family revision, hash, and variant. The model did not apply the patch, write an artifact, become canonical authority, or promote the family.",
  };
  const receipt: CogoDocumentTemplateFamilyModelInvocationReceipt = {
    ...receiptBasis,
    receiptHash:
      await hashCogoDocumentTemplateFamilyModelInvocationReceipt(receiptBasis),
  };
  return { proposal, receipt, prompt };
}
